import { useEffect, useMemo, useRef, useState } from "react";
import { permissionData } from "../../../assets/data";
import Helpers from "../../../commons/helpers";
import { ICodename, IPolicy, IResource, PolicyRequirement, RoleResourcePermission } from "../../../commons/interfaces";
import { Permission, PolicyRequirementType } from "../../../constants/enums";
import { useCommonComponentContext } from "../../../providers/CommonComponentProvider";
import PolicyService from "../../../services/identity/policy.service";

export type IPolicyError = { [k in keyof IPolicy]?: string };

export const useData = ({
    idDetail,
    getAllResource,
    getAllService,
    getAllGroup,
    getAllRole,
    getUsers,
}: {
    idDetail?: string;
    getAllResource: (serviceCode: string) => Promise<void>;
    getAllService: (searchText?: string) => Promise<void>;
    getAllGroup: (serviceCode: string) => Promise<void>;
    getAllRole: (serviceCode: string) => Promise<void>;
    getUsers: (ids: string[]) => Promise<void>;
}) => {
    const { clientId, userInfo, serviceCode, onError, onSuccess, onShowLoading, onHideLoading } = useCommonComponentContext();

    const acUpdate = useMemo(() => !Helpers.isNullOrEmpty(idDetail), [idDetail]);

    const permissionList = useMemo<ICodename[]>(() => permissionData, []);

    const permissionValues = useMemo<Permission[]>(
        () =>
            permissionList.reduce((acc, cur) => {
                acc.push(cur.code);
                return acc;
            }, []),
        [permissionList]
    );

    const fullPermission = useMemo<number>(() => permissionValues.reduce((acc, cur) => (acc = acc | cur), 0), [permissionValues]);

    const ref = useRef<IPolicy>();

    const [data, setData] = useState<IPolicy>({
        serviceCode,
        organizationId: userInfo?.userProfile?.organizationId || "0",
        clientId,
        requirements: [
            {
                name: "Group",
                type: PolicyRequirementType.Group,
                values: [],
            },
            {
                name: "Role",
                type: PolicyRequirementType.Role,
                values: [],
            },
            {
                name: "User",
                type: PolicyRequirementType.User,
                values: [],
            },
        ],
    } as IPolicy);

    const [error, setError] = useState<IPolicyError>({} as IPolicyError);

    useEffect(() => {
        const getDetail = async (id: string) => {
            const result = await PolicyService.getDetail(id);
            await getAllResource(result?.serviceCode);
            await getAllGroup(result?.serviceCode);
            await getAllRole(result?.serviceCode);
            getUsers((result.requirements || []).find((el) => el.type === PolicyRequirementType.User)?.values || []);
            const requirements = (result.requirements || []).map((el) => ({
                ...el,
                values: el.values ? el.values : ([] as string[]),
            }));

            setData({
                ...result,
                requirements:
                    requirements.length > 0
                        ? requirements
                        : ([
                              {
                                  name: "Group",
                                  type: PolicyRequirementType.Group,
                                  values: [],
                              },
                              {
                                  name: "Role",
                                  type: PolicyRequirementType.Role,
                                  values: [],
                              },
                              {
                                  name: "User",
                                  type: PolicyRequirementType.User,
                                  values: [],
                              },
                          ] as PolicyRequirement[]),
            });
            ref.current = result;
        };
        const getData = async () => {
            try {
                onShowLoading();
                await getAllService();

                if (Helpers.isNullOrEmpty(idDetail)) {
                    await getAllResource(serviceCode);
                    await getAllGroup(serviceCode);
                    await getAllRole(serviceCode);
                }
                if (!Helpers.isNullOrEmpty(idDetail)) await getDetail(idDetail);
            } catch (error) {
                const e = Helpers.renderExceptionError(error);
                onError(e);
            } finally {
                onHideLoading();
            }
        };
        getData();
    }, [userInfo?.userProfile?.organizationId, idDetail]);

    const onChangeValue = (key: keyof IPolicy) => async (value: string | number | RoleResourcePermission[] | PolicyRequirement[]) => {
        try {
            if (key === "serviceCode") {
                onShowLoading();
                setData((prev) => {
                    const temp = { ...prev };
                    Object.assign(temp, { [key]: value });
                    temp.resources = [];
                    temp.requirements = [];
                    return temp;
                });
                await getAllResource(value as string);
                await getAllGroup(value as string);
                await getAllRole(value as string);
                onHideLoading();
            }

            if (key !== "serviceCode") {
                setData((prev) => {
                    const temp = { ...prev };
                    Object.assign(temp, { [key]: value });
                    return temp;
                });
            }

            if (!Helpers.isNullOrEmpty(error[key])) setError((prev) => Object.assign(prev, { [key]: undefined }));
        } catch (error) {
            console.log({ error });
        } finally {
        }
    };

    const onAddRequirements = async (requirements: PolicyRequirement[]) => {
        try {
            onShowLoading();
            const ids = requirements.find((el) => el.type === PolicyRequirementType.User)?.values;
            await getUsers(ids);
            onChangeValue("requirements")(requirements);
        } catch (error) {
        } finally {
            onHideLoading();
        }
    };

    const onAddResource = (resourceData: IResource[]) => {
        const result: RoleResourcePermission[] = [];
        const temp = data?.resources || [];
        const resourceIds = [...temp].map((el) => el.resourceCode);
        for (const resource of resourceData) {
            if (resourceIds.includes(resource.resourceCode)) {
                result.push(temp.find((el) => el.resourceCode === resource.resourceCode));
                continue;
            }
            result.push({
                policyId: undefined,
                resourceCode: resource.resourceCode,
                permission: fullPermission,
                quota: undefined,
                id: undefined,
                status: undefined,
                createTime: undefined,
                createUser: undefined,
                updateTime: undefined,
                updateUser: undefined,
            } as RoleResourcePermission);
        }
        onChangeValue("resources")(result);
    };

    const isEdited = useMemo(() => JSON.stringify(ref.current) !== JSON.stringify(data), [data, ref.current]);

    return {
        data,
        error,
        isEdited,
        onChangeValue,

        acUpdate,

        setError,
        onAddRequirements,
        onAddResource,
    };
};

export default useData;
